import React, { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import hall1 from '../../Images/Emaar/hall1.png';
import hall2 from '../../Images/Emaar/hall2.png';
import hall3 from '../../Images/Emaar/hall3.png';
import hall4 from '../../Images/Emaar/hall4.png';
import hall5 from '../../Images/Emaar/hall6.png';
import hall6 from '../../Images/Emaar/hall7.png';

const images = [
  { src: hall1, label: 'حمام السباحة'     },
  { src: hall2, label: 'ملعب الجولف'      },
  { src: hall3, label: 'الصالة الداخلية'  },
  { src: hall4, label: 'المبنى الرئيسي'   },
  { src: hall5, label: 'منطقة الأطفال'    },
  { src: hall6, label: 'منطقة اليوغا'     },
];

export const EmaarHallPresenter = () => {
  const sectionRef  = useRef(null);
  const headerRef   = useRef(null);
  const gridRef     = useRef(null);
  const [lightbox, setLightbox] = useState(null);

  // Header animation
  useEffect(() => {
    const section = sectionRef.current;
    const header  = headerRef.current;
    if (!section || !header) return;

    gsap.set(header, { opacity: 0, y: 35 });

    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) gsap.to(header, { opacity: 1, y: 0, duration: 0.8, ease: 'power3.out' });
        else gsap.set(header, { opacity: 0, y: 35 });
      });
    }, { threshold: 0.1 });

    obs.observe(section);
    return () => obs.disconnect();
  }, []);

  // Grid cards animation
  useEffect(() => {
    const section = sectionRef.current;
    const grid    = gridRef.current;
    if (!section || !grid) return;

    const cards = grid.querySelectorAll('.hall-card');
    gsap.set(cards, { opacity: 0, y: 50, scale: 0.95 });

    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          gsap.to(cards, {
            opacity:  1,
            y:        0,
            scale:    1,
            duration: 0.7,
            ease:     'power3.out',
            stagger:  0.1,
          });
        } else {
          gsap.set(cards, { opacity: 0, y: 50, scale: 0.95 });
        }
      });
    }, { threshold: 0.1 });

    obs.observe(section);
    return () => obs.disconnect();
  }, []);

  // Close lightbox on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') setLightbox(null); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return (
    <section
      ref={sectionRef}
      style={{
        background:  '#0d0d0d',
        padding:     '90px 6vw',
        fontFamily:  "'Cairo', sans-serif",
        direction:   'rtl',
        overflow:    'hidden',
        position:    'relative',
      }}
    >
      {/* Background subtle pattern */}
      <div style={{
        position:   'absolute',
        inset:      0,
        background: 'radial-gradient(ellipse at 70% 50%, rgba(200,160,74,0.06) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      {/* Header */}
      <div ref={headerRef} style={{ marginBottom: '56px', opacity: 0, position: 'relative', zIndex: 2 }}>
        <p style={{
          fontSize:      '12px',
          fontWeight:    '600',
          color:         '#c8a04a',
          letterSpacing: '4px',
          textTransform: 'uppercase',
          margin:        '0 0 14px 0',
        }}>
          EMAAR GOLF FIELD
        </p>
        <h2 style={{
          fontSize:   'clamp(28px, 4vw, 52px)',
          fontWeight: '900',
          color:      '#fff',
          margin:     '0 0 16px 0',
          lineHeight: '1.2',
        }}>
          قاعة عرض
        </h2>
        <div style={{
          width:        '60px',
          height:       '2px',
          background:   'linear-gradient(to left, #c8a04a, #f0d080)',
          borderRadius: '2px',
        }} />
      </div>

      {/* Grid */}
      <div
        ref={gridRef}
        style={{
          display:             'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gridTemplateRows:    'repeat(2, 280px)',
          gap:                 '12px',
          position:            'relative',
          zIndex:              2,
        }}
      >
        {images.map((img, i) => (
          <div
            key={i}
            className="hall-card"
            onClick={() => setLightbox(img)}
            style={{
              position:     'relative',
              borderRadius: '12px',
              overflow:     'hidden',
              cursor:       'pointer',
              opacity:      0,
            }}
            onMouseEnter={e => {
              gsap.to(e.currentTarget.querySelector('img'), { scale: 1.08, duration: 0.5, ease: 'power2.out' });
              gsap.to(e.currentTarget.querySelector('.overlay'), { opacity: 1, duration: 0.3 });
              gsap.to(e.currentTarget.querySelector('.label'), { opacity: 1, y: 0, duration: 0.3 });
              gsap.to(e.currentTarget, { borderColor: 'rgba(200,160,74,0.6)', duration: 0.3 });
            }}
            onMouseLeave={e => {
              gsap.to(e.currentTarget.querySelector('img'), { scale: 1, duration: 0.5, ease: 'power2.out' });
              gsap.to(e.currentTarget.querySelector('.overlay'), { opacity: 0, duration: 0.3 });
              gsap.to(e.currentTarget.querySelector('.label'), { opacity: 0, y: 10, duration: 0.3 });
              gsap.to(e.currentTarget, { borderColor: 'rgba(255,255,255,0.05)', duration: 0.3 });
            }}
          >
            {/* Border */}
            <div style={{
              position:     'absolute',
              inset:        0,
              border:       '1px solid rgba(255,255,255,0.05)',
              borderRadius: '12px',
              zIndex:       3,
              pointerEvents: 'none',
            }} />

            {/* Image */}
            <img
              src={img.src}
              alt={img.label}
              style={{
                width:      '100%',
                height:     '100%',
                objectFit:  'cover',
                display:    'block',
                transformOrigin: 'center',
              }}
            />

            {/* Overlay */}
            <div
              className="overlay"
              style={{
                position:   'absolute',
                inset:      0,
                background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.1) 60%)',
                opacity:    0,
                zIndex:     2,
              }}
            />

            {/* Label */}
            <div
              className="label"
              style={{
                position:   'absolute',
                bottom:     '20px',
                right:      '20px',
                color:      '#fff',
                fontSize:   '15px',
                fontWeight: '700',
                opacity:    0,
                transform:  'translateY(10px)',
                zIndex:     3,
                textShadow: '0 2px 8px rgba(0,0,0,0.5)',
              }}
            >
              {img.label}
              <div style={{
                width:        '30px',
                height:       '2px',
                background:   '#c8a04a',
                borderRadius: '2px',
                marginTop:    '6px',
              }} />
            </div>

            {/* Corner accent */}
            <div style={{
              position:     'absolute',
              top:          '14px',
              left:         '14px',
              width:        '24px',
              height:       '24px',
              borderTop:    '2px solid rgba(200,160,74,0.6)',
              borderLeft:   '2px solid rgba(200,160,74,0.6)',
              borderRadius: '2px 0 0 0',
              zIndex:       3,
            }} />
            <div style={{
              position:      'absolute',
              bottom:        '14px',
              right:         '14px',
              width:         '24px',
              height:        '24px',
              borderBottom:  '2px solid rgba(200,160,74,0.6)',
              borderRight:   '2px solid rgba(200,160,74,0.6)',
              borderRadius:  '0 0 2px 0',
              zIndex:        3,
            }} />
          </div>
        ))}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          style={{
            position:       'fixed',
            inset:          0,
            background:     'rgba(0,0,0,0.92)',
            zIndex:         9999,
            display:        'flex',
            alignItems:     'center',
            justifyContent: 'center',
            padding:        '20px',
            backdropFilter: 'blur(8px)',
            animation:      'fadeInLightbox 0.25s ease',
          }}
        >
          <button
            onClick={() => setLightbox(null)}
            style={{
              position:   'absolute',
              top:        '24px',
              left:       '24px',
              background: 'rgba(255,255,255,0.1)',
              border:     '1px solid rgba(255,255,255,0.2)',
              color:      '#fff',
              width:      '44px',
              height:     '44px',
              borderRadius: '50%',
              fontSize:   '20px',
              cursor:     'pointer',
              display:    'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            ✕
          </button>

          <div style={{ textAlign: 'center' }}>
            <img
              src={lightbox.src}
              alt={lightbox.label}
              style={{
                maxWidth:     '90vw',
                maxHeight:    '80vh',
                borderRadius: '12px',
                objectFit:    'contain',
                display:      'block',
                margin:       '0 auto',
                border:       '1px solid rgba(200,160,74,0.3)',
              }}
            />
            <p style={{
              color:      '#c8a04a',
              fontSize:   '16px',
              fontWeight: '700',
              marginTop:  '16px',
              letterSpacing: '1px',
            }}>
              {lightbox.label}
            </p>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeInLightbox {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @media (max-width: 768px) {
          .hall-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            grid-template-rows: repeat(3, 200px) !important;
          }
        }
      `}</style>
    </section>
  );
};